import { Alert, Card, Skeleton, Stack, Text, Box } from '@chakra-ui/react';
import { isHttpError } from '@mono/http-hook';
import { FC } from 'react';
import FolderSection from './FolderSection';
import FileSection from './FileSection';
import { FileTreeResponse } from '../types';

const getErrorMessage = (error: unknown, fallback = 'Unable to load files.') => {
  if (isHttpError(error)) {
    const responseData = (error as any).response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as { error?: string; message?: string };
      return errorMessage ?? responseMessage ?? (error as any).message ?? fallback;
    }
    return (error as any).message ?? fallback;
  }

  if (error instanceof Error) return error.message;
  return fallback;
};

export const FileTreeLoading: FC = () => (
  <Stack gap="3">
    <Skeleton height="4rem" borderRadius="lg" />
    <Skeleton height="4rem" borderRadius="lg" />
    <Skeleton height="4rem" borderRadius="lg" />
  </Stack>
);

export const FileTreeError: FC<{ error: unknown }> = ({ error }) => (
  <Alert.Root status="error">
    <Alert.Indicator />
    <Alert.Content>
      <Alert.Title>Unable to load file tree</Alert.Title>
      <Alert.Description>{getErrorMessage(error)}</Alert.Description>
    </Alert.Content>
  </Alert.Root>
);

export const FileTreeEmptyOrg: FC = () => (
  <Alert.Root status="warning">
    <Alert.Indicator />
    <Alert.Content>
      <Alert.Title>Select an organization</Alert.Title>
      <Alert.Description>Choose an organization to browse project files.</Alert.Description>
    </Alert.Content>
  </Alert.Root>
);

export const FileTreePlaceholder: FC = () => (
  <Text color="fg.muted">Select a project path to view its contents.</Text>
);

export const FileTreeFolderView: FC<{
  response: Extract<FileTreeResponse, { type: 'folder' }>;
  onOpenFolder: (path: string) => void;
  onOpenFile: (path: string) => void;
  formatTimestamp: (value?: string | null) => string;
}> = ({ response, onOpenFile, onOpenFolder, formatTimestamp }) => {
  const { folders, files } = response.entries;
  const hasEntries = folders.length > 0 || files.length > 0;

  if (!hasEntries) {
    return (
      <Card.Root variant="subtle" borderStyle="dashed" borderWidth="2px" p="8">
        <Stack gap="1" textAlign="center">
          <Text fontWeight="semibold">This space looks empty</Text>
          <Text fontSize="sm" color="fg.muted">
            Create a folder or upload a file to start organizing your project.
          </Text>
        </Stack>
      </Card.Root>
    );
  }

  return (
    <Stack gap="6">
      <FolderSection folders={folders} onOpenFolder={onOpenFolder} formatTimestamp={formatTimestamp} />
      <Box borderTopWidth="1px" borderColor="border.subtle" />
      <FileSection files={files} onOpenFile={onOpenFile} formatTimestamp={formatTimestamp} />
    </Stack>
  );
};

